import { BorderRadius, Color, Spacing } from '@persona-id/design-tokens';
import { ShareIcon } from '@persona-id/design-tokens/icons';
import { XIcon } from '@persona-id/design-tokens/icons/12px';
import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';

import AsyncImage from 'dashboard/components-lib/AsyncImage';
import { FileWithPreview } from 'dashboard/components-lib/FileUpload/types';
import { DEFAULT_DOCUMENT_ALLOWED_FILE_TYPES } from 'dashboard/lib/stubs/verification-template-version';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button, { IconButton } from 'design-system/components/Button';
import { Paragraph } from 'design-system/components/Text';
import { fileTypeToExtension } from 'lib/file';

const Container = styled.div`
  display: flex;
  min-height: 440px;
  margin: ${Spacing[0]} ${Spacing[5]};
`;

const FilePreviewsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 100px);
  grid-auto-rows: 150px;
  gap: ${Spacing[2]};
`;

const StyledIconButton = styled(IconButton)`
  display: none;
  position: absolute;
  top: ${Spacing[2]};
  right: ${Spacing[2]};
  z-index: ${Z_INDICES.loadingOverlays};
  cursor: pointer;
`;

const FilePreview = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  width: 100%;
  padding: ${Spacing[1]};
  background-color: ${Color.BackgroundSecondary};
  border-radius: ${BorderRadius.Lg};

  &:hover {
    ${StyledIconButton} {
      display: block;
    }
  }
`;

const FileName = styled(Paragraph)`
  padding: ${Spacing[1]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledAsyncImage = styled(AsyncImage)`
  .react-pdf__Page {
    margin: 0;
  }
`;

const FileUploadLabelContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  padding: ${Spacing[4]};
  border: 2px dashed ${Color.BorderHairline};
  border-radius: ${BorderRadius.Lg};
  cursor: pointer;
`;

const FileUploadLabelContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing[2]};

  & > p {
    text-align: center;
  }
`;

interface MultiFileUploadProps {
  files: FileWithPreview[];
  onChange: (files: FileWithPreview[]) => void;
  className?: string;
}

const MultiFileUpload = ({ files, onChange, className }: MultiFileUploadProps) => {
  const onDropAccepted = useCallback(
    (acceptedFiles: File[]) => {
      const newFiles = acceptedFiles.map((file) => ({
        preview: URL.createObjectURL(file),
        name: file.name,
        type: file.type,
      })) as FileWithPreview[];

      onChange([...files, ...newFiles]);
    },
    [files, onChange],
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    onDropAccepted,
    accept: DEFAULT_DOCUMENT_ALLOWED_FILE_TYPES,
    multiple: true,
    noClick: true,
  });

  const handleDeleteFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    onChange(newFiles);
  };

  return (
    <Container {...getRootProps()} className={className}>
      <input {...getInputProps()} />
      {files.length === 0 ? (
        <FileUploadLabelContainer onClick={open} id='multi-file-upload__empty-state-container'>
          <FileUploadLabelContent>
            <ShareIcon color={Color.PrimaryPurple} />
            <Paragraph color='primary' size='lg' weight='bold'>
              Upload sample documents (up to 50mb)
            </Paragraph>
            <Paragraph size='sm'>
              {DEFAULT_DOCUMENT_ALLOWED_FILE_TYPES.map(fileTypeToExtension)
                .map((type) => type.toUpperCase())
                .join(', ')}
            </Paragraph>
            <Button variant='basic' width='auto'>
              Upload
            </Button>
          </FileUploadLabelContent>
        </FileUploadLabelContainer>
      ) : (
        <FilePreviewsContainer>
          {files.map((file, index) => (
            <FilePreview key={`${file.name}-${index}`} id={`multi-file-upload__preview-${index}`}>
              <StyledIconButton
                id='multi-file-upload__close-button'
                size='md'
                onClick={() => handleDeleteFile(index)}
              >
                <XIcon />
              </StyledIconButton>
              <StyledAsyncImage
                value={[
                  {
                    url: file.preview,
                    name: file.name,
                    type: file.type,
                    label: file.name,
                  },
                ]}
                width='auto'
                height='120px'
                showExtension={false}
                allowPreviewZoom={false}
                showCount={false}
              />
              <FileName size='sm' color='primary'>
                {file.name}
              </FileName>
            </FilePreview>
          ))}
          <FileUploadLabelContainer onClick={open} id='multi-file-upload__add-more-button'>
            <FileUploadLabelContent>
              <ShareIcon color={Color.PrimaryPurple} />
              <Paragraph size='xs'>Upload more sample documents</Paragraph>
            </FileUploadLabelContent>
          </FileUploadLabelContainer>
        </FilePreviewsContainer>
      )}
    </Container>
  );
};

export default MultiFileUpload;
